import React from 'react';
import PropTypes from 'prop-types';

import {Box, Link} from 'views/components';

const CommandTextViewer = (props) => {
  const {
    value,
    owners,
    connections,
    skill,
  } = props;

  const text = value || '';
  if (!text) {
    return null;
  }

  const uniqueByDisplay = (definitions) => {
    const map = new Map();

    definitions.forEach((definition) => {
      const display = definition?.display?.trim();

      if (!display) {
        return;
      }

      const key = display.toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          ...definition,
          display,
        });
      }
    });

    return [...map.values()];
  };

  const ownerDefinitions = uniqueByDisplay((owners || []).map((owner) => {
    const display = owner?.name || `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim() || owner?.userRn || '';

    if (!display) {
      return null;
    }

    return {
      display,
      href: '/admin/user',
    };
  }).filter(Boolean));

  const connectionDefinitions = uniqueByDisplay((connections || []).map((connection) => {
    const rn = connection?.RN || connection?.connectionRn || '';
    const display = connection?.name || `${connection?.firstName || ''} ${connection?.lastName || ''}`.trim() || rn;
    const entityType = connection?.type || (rn.includes('CLIENT') ? 'client' : rn.includes('CAREGIVER') ? 'caregiver' : null);

    if (!display) {
      return null;
    }

    return {
      display,
      type: entityType,
      href: entityType === 'client' ?
        (rn ? `/client/${rn}` : null) :
        entityType === 'caregiver' ?
          (rn ? `/caregiver/${rn}` : null) :
          null,
    };
  }).filter(Boolean));

  const definitionsByTrigger = {
    '@': {
      color: '#958ACC',
      definitions: ownerDefinitions,
    },
    '#': {
      color: '#958ACC',
      definitions: connectionDefinitions.filter((definition) => definition.type === 'caregiver'),
    },
    '$': {
      color: '#958ACC',
      definitions: connectionDefinitions.filter((definition) => definition.type === 'client'),
    },
    '/': {
      color: '#B45309',
      definitions: skill ? [{display: skill}] : [],
    },
  };

  const matchCommandAt = (startIndex) => {
    const trigger = text[startIndex];
    const definitions = definitionsByTrigger[trigger]?.definitions;

    if (!definitions?.length) {
      return null;
    }

    if (startIndex > 0 && !/\s/.test(text[startIndex - 1])) {
      return null;
    }

    const trailingText = text.slice(startIndex + 1);
    const trailingLower = trailingText.toLowerCase();
    let bestMatch = null;

    definitions.forEach((definition) => {
      const matchValue = definition.display.toLowerCase();

      if (!trailingLower.startsWith(matchValue)) {
        return;
      }

      const nextChar = trailingText[matchValue.length];
      if (nextChar && !/[\s,.;:!?)]/.test(nextChar)) {
        return;
      }

      if (!bestMatch || matchValue.length > bestMatch.length) {
        bestMatch = {
          trigger,
          definition,
          length: matchValue.length + 1,
        };
      }
    });

    if (!bestMatch) {
      return null;
    }

    return {
      trigger: bestMatch.trigger,
      definition: bestMatch.definition,
      index: startIndex,
      length: bestMatch.length,
    };
  };

  const commandRanges = [];
  let scanIndex = 0;

  while (scanIndex < text.length) {
    const match = matchCommandAt(scanIndex);

    if (!match) {
      scanIndex += 1;
      continue;
    }

    commandRanges.push(match);
    scanIndex += match.length;
  }

  const segments = [];
  let cursor = 0;

  commandRanges.forEach((range, index) => {
    if (range.index > cursor) {
      segments.push({
        key: `plain-${index}-${cursor}`,
        text: text.slice(cursor, range.index),
      });
    }

    segments.push({
      key: `command-${index}-${range.index}`,
      text: text.slice(range.index, range.index + range.length),
      color: definitionsByTrigger[range.trigger]?.color,
      href: range.definition?.href || null,
    });

    cursor = range.index + range.length;
  });

  if (cursor < text.length) {
    segments.push({
      key: `plain-tail-${cursor}`,
      text: text.slice(cursor),
    });
  }

  return (
    <Box component='span'>
      {
        segments.map((segment) => {
          if (segment.href) {
            return (
              <Link
                key={segment.key}
                href={segment.href}
                target='_blank'
                rel='noopener noreferrer'
                underline='hover'
                onClick={(event) => event.stopPropagation()}
                sx={{
                  color: segment.color,
                  cursor: 'pointer',
                }}
              >
                {segment.text}
              </Link>
            );
          }

          return (
            <Box
              key={segment.key}
              component='span'
              sx={segment.color ? {color: segment.color} : undefined}
            >
              {segment.text}
            </Box>
          );
        })
      }
    </Box>
  );
};

CommandTextViewer.propTypes = {
  connections: PropTypes.array,
  owners: PropTypes.array,
  value: PropTypes.string,
  skill: PropTypes.string,
};

export default React.memo(CommandTextViewer);
